import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, addDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Clock, Plus, X, ChevronLeft, ChevronRight, Users, LayoutGrid, List, Eye } from 'lucide-react';
import { getWeekAvailability, createAvailabilityRecord } from './utils/availabilityUtils';
import { getWeekStart, getWeekDates, VIEW_RANGES, getDateRange, chunkIntoWeeks, navigateRange, parseLocalDate } from './utils/schedulingEngine';
import { isManager } from './utils/permissions';
import LoadingSpinner from './components/LoadingSpinner';

const DAY_NAME_KEY_BY_INDEX = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const FULL_DAY_NAME_KEY_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function AvailabilityManager({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date().toISOString().split('T')[0], 1));
  const [viewRange, setViewRange] = useState('1w');
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [allStaffAvailability, setAllStaffAvailability] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);

  const weekDates = getWeekDates(weekStart);
  const allDates = getDateRange(weekStart, viewRange);
  const weekChunks = chunkIntoWeeks(allDates);
  const userIsManager = isManager(userData?.role);
  const shortDayNameForDate = (date) => t(`availability.${DAY_NAME_KEY_BY_INDEX[parseLocalDate(date).getDay()]}`);
  const fullDayNameForDayIndex = (dayIndex) => t(`availability.${FULL_DAY_NAME_KEY_BY_INDEX[dayIndex]}`);

  useEffect(() => {
    if (!companyId || !user) return;

    const q = query(
      collection(db, 'companies', companyId, 'availability'),
      where('staffId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setAvailability(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error listening to availability:', error);
      setLoading(false);
    });

    return unsub;
  }, [companyId, user]);

  useEffect(() => {
    if (!companyId) return;
    const unsubSettings = onSnapshot(doc(db, 'companies', companyId), (snap) => {
      const nextWeekStartDay = snap.data()?.shiftsSettings?.weekStartDay ?? snap.data()?.shiftsSettings?.scheduling?.weekStartDay ?? 1;
      setWeekStartDay(nextWeekStartDay);
      setWeekStart(prev => getWeekStart(prev || new Date().toISOString().split('T')[0], nextWeekStartDay));
    }, (error) => {
      console.error('Error listening to availability settings:', error);
    });
    return () => unsubSettings();
  }, [companyId]);

  // Load all staff availability for heatmap (managers only)
  useEffect(() => {
    if (!companyId || !userIsManager || !showHeatmap) return;
    setHeatmapLoading(true);

    // Load all staff
    const usersQ = query(collection(db, 'companies', companyId, 'users'));
    const unsubUsers = onSnapshot(usersQ, snap => {
      setStaffList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});

    // Load all availability
    const allAvailQ = query(collection(db, 'companies', companyId, 'availability'));
    const unsubAllAvail = onSnapshot(allAvailQ, snap => {
      setAllStaffAvailability(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHeatmapLoading(false);
    }, () => setHeatmapLoading(false));

    return () => { unsubUsers(); unsubAllAvail(); };
  }, [companyId, userIsManager, showHeatmap]);

  // Calculate heatmap data: for each date/hour, how many staff are available
  const heatmapData = useMemo(() => {
    if (!showHeatmap || staffList.length === 0) return {};
    const data = {};
    const totalStaff = staffList.length;

    allDates.forEach(date => {
      data[date] = {};
      HOURS.forEach(hour => {
        // Count staff who have availability blocks that make them unavailable
        let unavailableCount = 0;
        staffList.forEach(staff => {
          const staffRecords = allStaffAvailability.filter(r => {
            if (r.staffId !== staff.id) return false;
            if (r.recurrence === 'none') return r.date === date;
            if (r.recurrence === 'weekly') return r.dayOfWeek === parseLocalDate(date).getDay();
            return false;
          });
          const isUnavail = staffRecords.some(r => {
            if (r.type !== 'unavailable' && r.type !== 'leave') return false;
            if (!r.startTime || !r.endTime) return true; // all day
            const startH = parseInt(r.startTime.split(':')[0]);
            const endH = parseInt(r.endTime.split(':')[0]);
            return hour >= startH && hour < endH;
          });
          if (isUnavail) unavailableCount++;
        });
        data[date][hour] = totalStaff - unavailableCount;
      });
    });
    return data;
  }, [showHeatmap, allDates, staffList, allStaffAvailability]);

  const getHeatColor = (available, total) => {
    if (total === 0) return 'bg-gray-600';
    const ratio = available / total;
    if (ratio >= 0.8) return 'bg-green-500';
    if (ratio >= 0.6) return 'bg-green-400/80';
    if (ratio >= 0.4) return 'bg-amber-400';
    if (ratio >= 0.2) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const navigateView = (direction) => {
    setWeekStart(navigateRange(weekStart, direction, viewRange));
  };

  const weekAvailability = getWeekAvailability(user.uid, weekStart, availability);

  const addAvailabilityRecord = async (data) => {
    try {
      const record = createAvailabilityRecord(user.uid, data.type, {
        recurrence: data.recurrence,
        date: data.date,
        dayOfWeek: data.dayOfWeek,
        startTime: data.startTime,
        endTime: data.endTime,
        reason: data.reason,
        startDate: data.startDate,
      });

      await addDoc(collection(db, 'companies', companyId, 'availability'), record);
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability');
    }
  };

  const deleteRecord = async (recordId) => {
    if (!window.confirm(t('availability.confirmDelete'))) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'availability', recordId));
    } catch (error) {
      console.error('Error deleting availability:', error);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Clock size={24} className="text-primary" />
              {t('availability.title')}
            </h1>
            <p className="text-gray-400 mt-1">{t('availability.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* View range selector */}
            <select
              value={viewRange}
              onChange={e => setViewRange(e.target.value)}
              className="bg-white/5 border border-white/20 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-primary/50 focus:border-primary [color-scheme:dark]"
            >
              {VIEW_RANGES.map(r => (
                <option key={r.key} value={r.key} className="bg-slate-700 text-white">{t(r.labelKey)}</option>
              ))}
            </select>
            <button onClick={() => navigateView(-1)} className="p-2 rounded-lg hover:bg-white/10"><ChevronLeft size={20} /></button>
            <span className="text-sm font-medium text-gray-300">
              {parseLocalDate(allDates[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              {' – '}
              {parseLocalDate(allDates[allDates.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <button onClick={() => navigateView(1)} className="p-2 rounded-lg hover:bg-white/10"><ChevronRight size={20} /></button>
            <button
              onClick={() => setWeekStart(getWeekStart(new Date().toISOString().split('T')[0], weekStartDay))}
              className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5"
            >
              {t('common.today')}
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              <Plus size={16} /> {t('availability.setAvailability')}
            </button>
            {userIsManager && (
              <button
                onClick={() => setShowHeatmap(!showHeatmap)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showHeatmap ? 'bg-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
              >
                <Eye size={16} /> {t('availability.heatmap', 'Coverage Heatmap')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Coverage Heatmap (Managers) */}
      {showHeatmap && userIsManager && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Users size={18} className="text-primary" />
            {t('availability.coverageHeatmap', 'Staff Coverage Heatmap')}
            <span className="text-xs text-gray-400 ml-2">({staffList.length} {t('common.staff', 'staff')})</span>
          </h2>
          {heatmapLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Legend */}
              <div className="flex items-center gap-3 mb-4 text-xs text-gray-400">
                <span>{t('availability.coverage', 'Coverage')}:</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500" /> 0-20%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500" /> 20-40%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400" /> 40-60%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-400/80" /> 60-80%</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500" /> 80-100%</span>
              </div>
              {/* Heatmap grid */}
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="text-left text-gray-400 p-1 w-20">{t('common.date', 'Date')}</th>
                      {HOURS.filter(h => h >= 6 && h <= 22).map(h => (
                        <th key={h} className="text-center text-gray-400 p-1 min-w-[28px]">{h}:00</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allDates.slice(0, viewRange === '1m' ? 31 : viewRange === '2w' ? 14 : 7).map(date => (
                      <tr key={date}>
                        <td className="text-gray-300 p-1 whitespace-nowrap">
                          {parseLocalDate(date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                        </td>
                        {HOURS.filter(h => h >= 6 && h <= 22).map(h => {
                          const available = heatmapData[date]?.[h] || 0;
                          return (
                            <td key={h} className="p-0.5" title={`${available}/${staffList.length} available`}>
                              <div className={`w-full h-6 rounded ${getHeatColor(available, staffList.length)} opacity-80 flex items-center justify-center`}>
                                <span className="text-[9px] text-white font-medium">{available}</span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Week view — repeated per week chunk */}
      <div className="space-y-4">
        {weekChunks.map((weekDts, wIdx) => {
          const weekLabel = new Date(weekDts[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            + ' – ' + new Date(weekDts[weekDts.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          const safeWeekLabel = parseLocalDate(weekDts[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            + ' - ' + parseLocalDate(weekDts[weekDts.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          return (
            <div key={wIdx}>
              {viewRange !== '1w' && (
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 pl-1">{safeWeekLabel}</p>
              )}
              <div className="grid grid-cols-7 gap-2">
                {weekDts.map((date, idx) => {
                  const dayAvail = availability.find(r => {
                    if (r.recurrence === 'none') return r.date === date;
                    if (r.recurrence === 'weekly') return r.dayOfWeek === parseLocalDate(date).getDay();
                    return false;
                  });
                  const isAvailable = dayAvail ? dayAvail.type !== 'unavailable' && dayAvail.type !== 'leave' : true;
                  const isToday = date === new Date().toISOString().split('T')[0];
                  const dayRecords = availability.filter(r => {
                    if (r.recurrence === 'none') return r.date === date;
                    if (r.recurrence === 'weekly') return r.dayOfWeek === parseLocalDate(date).getDay();
                    return false;
                  });

                  return (
                    <div
                      key={date}
                      className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow ${
                        isToday ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => { setSelectedDate(date); setShowAddModal(true); }}
                    >
                      <div className="text-center mb-3">
                        <p className="text-xs font-medium text-gray-400 uppercase">{shortDayNameForDate(date)}</p>
                        <p className="text-lg font-bold text-white">{parseLocalDate(date).getDate()}</p>
                        {viewRange !== '1w' && (
                          <p className="text-[10px] text-gray-400">
                            {parseLocalDate(date).toLocaleDateString(undefined, { month: 'short' })}
                          </p>
                        )}
                      </div>
                      <div className={`w-full h-2 rounded-full ${
                        isAvailable ? 'bg-green-400' : 'bg-red-400'
                      }`} />
                      <p className={`text-xs text-center mt-2 font-medium ${
                        isAvailable ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {isAvailable ? t('availability.available') : t('availability.unavailable')}
                      </p>

                      {/* Show records */}
                      {dayRecords.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {dayRecords.map(record => (
                            <div key={record.id} className="flex items-center justify-between bg-white/5 rounded px-2 py-1 text-xs">
                              <span className={record.type === 'leave' ? 'text-red-600' : 'text-yellow-600'}>
                                {record.startTime && record.endTime ? `${record.startTime}–${record.endTime}` : t('availability.allDay')}
                              </span>
                              <button onClick={(e) => { e.stopPropagation(); deleteRecord(record.id); }} className="text-gray-400 hover:text-red-500">
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Existing records */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">{t('availability.yourRules')}</h2>
        {availability.length === 0 ? (
          <p className="text-gray-400 text-center py-4">{t('availability.noRules')}</p>
        ) : (
          <div className="space-y-2">
            {availability.map(record => (
              <div key={record.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-white capitalize">{t(`availability.${record.type}`) || record.type}</p>
                  <p className="text-xs text-gray-400">
                    {record.recurrence === 'none' ? record.date : `${t(`availability.${record.recurrence}`)} — ${fullDayNameForDayIndex(record.dayOfWeek)}`}
                    {record.startTime && ` · ${record.startTime}–${record.endTime}`}
                    {record.reason && ` — ${record.reason}`}
                  </p>
                </div>
                <button onClick={() => deleteRecord(record.id)} className="p-1 text-gray-400 hover:text-red-500">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Availability Modal */}
      {showAddModal && (
        <AvailabilityModal
          date={selectedDate}
          weekStartDay={weekStartDay}
          onClose={() => { setShowAddModal(false); setSelectedDate(null); }}
          onSave={addAvailabilityRecord}
        />
      )}
    </div>
  );
}

function AvailabilityModal({ date, weekStartDay = 1, onClose, onSave }) {
  const { t } = useTranslation();
  const fullDayOptions = Array.from({ length: 7 }, (_, idx) => {
    const dayIndex = (weekStartDay + idx) % 7;
    return {
      value: dayIndex,
      label: t(`availability.${FULL_DAY_NAME_KEY_BY_INDEX[dayIndex]}`),
    };
  });
  const [formData, setFormData] = useState({
    type: 'unavailable',
    recurrence: 'none',
    date: date || '',
    dayOfWeek: date ? parseLocalDate(date).getDay() : 1,
    startTime: '',
    endTime: '',
    reason: '',
    allDay: true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      startTime: formData.allDay ? null : formData.startTime,
      endTime: formData.allDay ? null : formData.endTime,
      startDate: formData.date,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-white/10 rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">{t('availability.setAvailability')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.type')}</label>
            <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}
              className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2 appearance-none">
              <option value="unavailable" className="bg-slate-700 text-white">{t('availability.unavailable')}</option>
              <option value="leave" className="bg-slate-700 text-white">{t('availability.leave')}</option>
              <option value="preferred" className="bg-slate-700 text-white">{t('availability.preferred')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.recurrence')}</label>
            <select value={formData.recurrence} onChange={e => setFormData({ ...formData, recurrence: e.target.value })}
              className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2 appearance-none">
              <option value="none" className="bg-slate-700 text-white">{t('availability.oneOff')}</option>
              <option value="weekly" className="bg-slate-700 text-white">{t('availability.weekly')}</option>
              <option value="biweekly" className="bg-slate-700 text-white">{t('availability.biweekly')}</option>
            </select>
          </div>
          {formData.recurrence === 'none' ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.date')}</label>
              <input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2" required />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.dayOfWeek')}</label>
              <select value={formData.dayOfWeek} onChange={e => setFormData({ ...formData, dayOfWeek: Number(e.target.value) })}
                className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2 appearance-none">
                {fullDayOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-slate-700 text-white">{option.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={formData.allDay} onChange={e => setFormData({ ...formData, allDay: e.target.checked })}
              className="rounded" id="allDay" />
            <label htmlFor="allDay" className="text-sm text-gray-300">{t('availability.allDay')}</label>
          </div>
          {!formData.allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.from')}</label>
                <input type="time" value={formData.startTime} onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                  className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.to')}</label>
                <input type="time" value={formData.endTime} onChange={e => setFormData({ ...formData, endTime: e.target.value })}
                  className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2" required />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('availability.reason')}</label>
            <input type="text" value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })}
              className="w-full bg-slate-700 border border-white/20 rounded-lg text-white px-3 py-2" placeholder={t('availability.optional')} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="flex-1 bg-primary text-white rounded-lg py-2 font-medium hover:bg-primary-dark transition-colors">{t('common.save')}</button>
            <button type="button" onClick={onClose} className="flex-1 bg-white/10 text-gray-300 rounded-lg py-2 font-medium hover:bg-white/20 transition-colors">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
